import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BarChart2, TrendingUp, Tag, Clock, RefreshCw, AlertTriangle } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

/**
 * Sprint 9 — Advanced Analytics (Pro-only). 4 tabs: Trend, Cohort,
 * Keywords, Timing. All server-gated by `require_feature("advanced_analytics")`.
 */
const TABS = [
  { id: "trend", label: "Trend", icon: TrendingUp },
  { id: "cohort", label: "Cohort", icon: BarChart2 },
  { id: "keywords", label: "Keyword", icon: Tag },
  { id: "timing", label: "Timing", icon: Clock },
];

const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function AdvancedAnalyticsSection({ businessId, dateRange }) {
  const [tab, setTab] = useState("trend");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(() => {
    const p = { business_id: businessId };
    if (dateRange?.from) p.start = new Date(dateRange.from).toISOString();
    if (dateRange?.to) p.end = new Date(dateRange.to).toISOString();
    return p;
  }, [businessId, dateRange]);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.get(`/analytics/${tab}`, { params });
      setData((s) => ({ ...s, [tab]: d }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) setError("Serve un piano Pro attivo.");
      else setError("Impossibile caricare gli analytics.");
    } finally { setLoading(false); }
  }, [tab, businessId, params]);

  useEffect(() => { load(); }, [load]);

  const active = data[tab];

  return (
    <motion.section
      initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      data-testid="analytics-section"
      className="rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F2F4] font-sans font-semibold flex items-center gap-2">
            <BarChart2 size={12} strokeWidth={2.2} /> Analytics avanzati
          </div>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-[var(--fg-1)] tracking-tight">
            Numeri che raccontano una storia
          </h3>
        </div>
        <div className="flex gap-1 rounded-full border border-[var(--border-c)] bg-[var(--bg-3)] p-1" data-testid="analytics-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id} onClick={() => setTab(t.id)}
                data-testid={`analytics-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-sans ${
                  isActive ? "bg-[#00F2F4] text-[#0A0A0A] font-semibold" : "text-[var(--fg-2)] hover:text-[var(--fg-1)]"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                <Icon size={12} strokeWidth={2.2} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 min-h-[280px]">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-[var(--fg-3)]">
            <RefreshCw size={14} className="animate-spin" /> Caricamento…
          </div>
        )}
        {error && !loading && (
          <div className="rounded-xl border border-[#C6423B]/40 bg-[#C6423B]/10 p-4 text-sm flex items-start gap-2 text-[var(--fg-2)]">
            <AlertTriangle size={16} className="text-[#C6423B] mt-0.5" /> {error}
          </div>
        )}
        {!loading && !error && active && (
          <>
            {tab === "trend" && (
              <div data-testid="analytics-trend" className="h-72">
                {active.points?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={active.points}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-c)" />
                      <XAxis dataKey="date" tick={{ fill: "var(--fg-3)", fontSize: 10 }} />
                      <YAxis yAxisId="left" tick={{ fill: "var(--fg-3)", fontSize: 10 }} domain={[0, 5]} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: "var(--fg-3)", fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: "var(--bg-2)", border: "1px solid var(--border-c)" }} />
                      <Line yAxisId="left" type="monotone" dataKey="avg_rating" stroke="#00F2F4" strokeWidth={2} name="Media stelle" dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="count" stroke="#E9A94E" strokeWidth={2} name="N. recensioni" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyState label="Nessun dato nel periodo" />}
              </div>
            )}
            {tab === "cohort" && (
              <div data-testid="analytics-cohort" className="h-72">
                {active.cohorts?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={active.cohorts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-c)" />
                      <XAxis dataKey="week" tick={{ fill: "var(--fg-3)", fontSize: 10 }} />
                      <YAxis tick={{ fill: "var(--fg-3)", fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: "var(--bg-2)", border: "1px solid var(--border-c)" }} />
                      <Bar dataKey="stars_5" stackId="a" fill="#4A8A6E" name="5★" />
                      <Bar dataKey="stars_4" stackId="a" fill="#7EAA88" name="4★" />
                      <Bar dataKey="stars_3" stackId="a" fill="#E9A94E" name="3★" />
                      <Bar dataKey="stars_2" stackId="a" fill="#D97C4A" name="2★" />
                      <Bar dataKey="stars_1" stackId="a" fill="#C6423B" name="1★" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState label="Nessun dato per la cohort" />}
              </div>
            )}
            {tab === "keywords" && (
              <div data-testid="analytics-keywords" className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {active.keywords?.length ? active.keywords.slice(0, 24).map((k) => (
                  <div key={k.keyword} className="rounded-lg border border-[var(--border-c)] bg-[var(--bg-3)] px-3 py-2 flex items-center justify-between">
                    <span className="text-sm text-[var(--fg-1)] font-sans truncate">{k.keyword}</span>
                    <span className="text-[10px] text-[#00F2F4] font-sans font-semibold ml-2">{k.count}</span>
                  </div>
                )) : <EmptyState label="Nessuna keyword rilevata" />}
              </div>
            )}
            {tab === "timing" && (
              <div data-testid="analytics-timing" className="overflow-x-auto">
                <TimingHeatmap cells={active.cells || []} />
              </div>
            )}
          </>
        )}
      </div>
    </motion.section>
  );
}

function EmptyState({ label }) {
  return <div className="text-sm text-[var(--fg-3)] italic py-10 text-center">{label}</div>;
}

function TimingHeatmap({ cells }) {
  const max = Math.max(1, ...cells.map((c) => c.count));
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  cells.forEach((c) => { grid[c.weekday][c.hour] = c.count; });
  return (
    <table className="min-w-full text-xs font-sans">
      <thead>
        <tr>
          <th className="text-left text-[var(--fg-3)] pr-2"></th>
          {Array.from({ length: 24 }, (_, h) => (
            <th key={h} className="text-[var(--fg-3)] font-normal text-[9px] py-1 w-6">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {DAY_NAMES.map((d, i) => (
          <tr key={d}>
            <td className="text-[var(--fg-3)] pr-2 py-0.5 text-[10px]">{d}</td>
            {grid[i].map((v, h) => {
              const intensity = v / max;
              return (
                <td key={h} className="w-6 h-6 text-center align-middle text-[9px]"
                    style={{
                      backgroundColor: v > 0 ? `rgba(0, 242, 244, ${Math.max(0.15, intensity)})` : "transparent",
                      color: v > 0 ? "#0A0A0A" : "var(--fg-3)",
                    }}>
                  {v || ""}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
